// Global fetch guard: transparently refresh the access token when any
// same-origin /api/* call comes back 401, then retry the original request
// once. Installed at app bootstrap so raw `fetch('/api/...')` calls that
// don't go through utils/api.js inherit the same behavior.
//
// Motivation: access tokens expire after 15 minutes. Long-lived screens
// (Virtual Classroom, presentation editor) frequently outlive that window,
// and any raw fetch would surface a "Необхідна авторизація" toast at the
// worst possible moment (e.g. teacher submitting a post-lesson report).

const RETRY_HEADER = 'x-lingvish-retry';

const INSTALL_FLAG = '__lingvishFetchGuardInstalled';
let refreshPromise = null;

const shouldGuard = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    if (!path.startsWith('/api/')) return false;
    // /api/auth/* has its own semantics (login 401 = wrong password, etc).
    if (path.startsWith('/api/auth/')) return false;
    return true;
  } catch {
    return false;
  }
};

const wasRetried = (init) => {
  const h = init?.headers;
  if (!h) return false;
  if (typeof h.get === 'function') return h.get(RETRY_HEADER) === '1';
  if (Array.isArray(h)) return h.some(([k, v]) => k?.toLowerCase() === RETRY_HEADER && v === '1');
  return h[RETRY_HEADER] === '1';
};

const withRetryHeader = (init) => {
  const base = init || {};
  const headers = new Headers(base.headers || {});
  headers.set(RETRY_HEADER, '1');
  return { ...base, headers };
};

export function installFetchAuthGuard() {
  if (typeof window === 'undefined' || !window.fetch) return;
  if (window[INSTALL_FLAG]) return;
  window[INSTALL_FLAG] = true;

  const originalFetch = window.fetch.bind(window);

  const refreshOnce = () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const res = await originalFetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        return res.ok;
      } catch {
        return false;
      } finally {
        // Release on the next tick so requests that 401 in the same
        // microtask batch all await the same refresh promise.
        setTimeout(() => { refreshPromise = null; }, 0);
      }
    })();
    return refreshPromise;
  };

  window.fetch = async (input, init) => {
    // Only string URLs get the retry treatment. Request objects have
    // one-shot bodies that can't be safely re-sent without cloning
    // upstream, so pass them through as-is.
    const isStringInput = typeof input === 'string';
    const url = isStringInput ? input : (input?.url || '');
    const guarded = isStringInput && shouldGuard(url);

    const finalInit = guarded
      ? { credentials: 'include', ...init }
      : init;

    const res = await originalFetch(input, finalInit);

    if (res.status !== 401 || !guarded || wasRetried(finalInit)) {
      return res;
    }

    const refreshed = await refreshOnce();
    if (!refreshed) return res;

    return originalFetch(input, withRetryHeader(finalInit));
  };
}

export default installFetchAuthGuard;
